import _ from 'lodash'
import { createContext, forwardRef, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { createTheme, useTheme, ThemeProvider } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Stepper from '@mui/material/Stepper'
import { StepProps } from '@mui/material/Step'

import AddressStep from './AddressStep'
import DateTimeStep, { DateTimeStepProps } from './DateTimeStep'
import PatientInfoStep from './PatientInfoStep'
import SpecialityStep from './SpecialityStep'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { GooglePlacesAutocompleteValue } from '@/components/formik/fields/GooglePlacesAutocompleteField'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'

const bookingFormMessages = {
  addressRequired: msg`Address is required`,
}

export type BookingFormHandle = {
  reset: () => void
}

export type BookingFormContextType = {
  isMobile?: boolean
  onComplete: () => void
  onEditStep: (step: number) => void
  onNextStep: () => void
  onPreviousStep: () => void
}

export type BookingFormProps = {
  onSubmit: (values: BookingFormValues) => void
  specialities: string[]
  schedules: DateTimeStepProps['schedules']
}

export type BookingFormValues = {
  address: GooglePlacesAutocompleteValue
  meetingPlace: AvailabilityMeetingPlace
  patientInfo: {
    email: string
    firstName: string
    lastName: string
    phone: string
  }
  speciality: string
  startDate: string
}

export const BookingFormContext = createContext<BookingFormContextType>({
  onComplete: () => { },
  onEditStep: () => { },
  onNextStep: () => { },
  onPreviousStep: () => { },
})

export const BookingForm = forwardRef(function BookingForm(props: BookingFormProps, ref: Ref<BookingFormHandle>) {
  const { onSubmit, specialities, schedules } = props
  const defaultTheme = useTheme()
  const { i18n } = useLingui()
  const isMobileScreen = useMediaQuery(defaultTheme.breakpoints.down('sm'), { noSsr: true })

  const formikRef = useRef<FormikProps<BookingFormValues>>(null)

  const [activeStep, setActiveStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<{ [k: number]: boolean }>({})

  const initialValues = useMemo(
    () => ({
      address: null,
      meetingPlace: AvailabilityMeetingPlace.CENTER,
      patientInfo: {
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
      },
      speciality: '',
      startDate: '',
    }),
    []
  )

  const specialityOptions = useMemo(() => _.map(specialities, (speciality) => ({ label: speciality, value: speciality })), [specialities])

  const steps = useMemo(
    () => [
      { component: (stepProps: StepProps) => <SpecialityStep {...stepProps} specialityOptions={specialityOptions} /> },
      { component: (stepProps: StepProps) => <AddressStep {...stepProps} schedules={schedules} /> },
      { component: (stepProps: StepProps) => <DateTimeStep {...stepProps} schedules={schedules} /> },
      { component: (stepProps: StepProps) => <PatientInfoStep {...stepProps} /> },
    ],
    [specialityOptions, schedules]
  )

  const theme = useMemo(
    () =>
      createTheme(defaultTheme, {
        components: {
          MuiStepContent: {
            styleOverrides: {
              root: {
                borderLeft: 'none',
                marginLeft: 0,
                paddingLeft: 0,
              },
              transition: {
                height: '100%',
                '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                  height: '100%',
                },
              },
            },
          },
        },
        typography: {
          h3: {
            color: colors.gray400,
            fontSize: '23px',
            fontWeight: 500,
          },
        },
      }),
    [defaultTheme]
  )

  const validationSchema = useMemo(
    () =>
      yup.object({
        address: yup
          .object({
            description: yup.string(),
            placeId: yup.string(),
          })
          .nullable()
          .required(i18n._(bookingFormMessages.addressRequired)),
        meetingPlace: yup.string().required(),
        patientInfo: yup.object({
          email: yup.string().email().required(),
          firstName: yup.string().required(),
          lastName: yup.string().required(),
          phone: yup.string().required(),
        }),
        speciality: yup.string().required(),
        startDate: yup.string().required(),
      }),
    [i18n, i18n.locale]
  )

  const onComplete = useCallback(() => {
    formikRef.current?.submitForm()
  }, [])

  const onEditStep = useCallback((editingStep: number) => {
    setActiveStep((prevStep) => {
      setCompletedSteps((prevCompletedSteps) =>
        _.reduce(
          prevCompletedSteps,
          (nextCompletedSteps, _completed, step) => {
            if (Number(step) >= editingStep && Number(step) < prevStep) nextCompletedSteps[Number(step)] = false
            return nextCompletedSteps
          },
          { ...prevCompletedSteps }
        )
      )
      return editingStep
    })
  }, [])

  const onNextStep = useCallback(() => {
    setActiveStep((prevStep) => {
      setCompletedSteps((prevCompletedStep) => ({ ...prevCompletedStep, [prevStep]: true }))
      return prevStep + 1
    })
  }, [])

  const onPreviousStep = useCallback(() => {
    setActiveStep((prevStep) => {
      const nextStep = Math.max(prevStep - 1, 0)
      setCompletedSteps((prevCompletedStep) => ({ ...prevCompletedStep, [nextStep]: false }))
      return nextStep
    })
  }, [])

  const value: BookingFormContextType = useMemo(
    () => ({
      isMobile: isMobileScreen,
      onComplete,
      onEditStep,
      onNextStep,
      onPreviousStep,
    }),
    [isMobileScreen, onComplete, onEditStep, onNextStep, onPreviousStep]
  )

  useImperativeHandle(ref, () => ({
    reset: () => {
      formikRef.current?.resetForm({ values: initialValues })
      setActiveStep(0)
      setCompletedSteps({})
    },
  }))

  return (
    <ThemeProvider theme={theme}>
      <BookingFormContext.Provider value={value}>
        <Formik enableReinitialize initialValues={initialValues} innerRef={formikRef} onSubmit={onSubmit} validationSchema={validationSchema}>
          {() => (
            <Form style={{ flex: 1, padding: '10px 16px 0' }}>
              <Stepper activeStep={activeStep} connector={null} orientation="vertical" sx={{ height: '100%' }}>
                {steps.map(({ component: Component }, index) => (
                  <Component
                    key={index}
                    active={activeStep === index}
                    completed={!!completedSteps[index]}
                    sx={[activeStep === index && isMobileScreen && { height: '100%' }, isMobileScreen && { '& .MuiStepContent-root': { pr: 0 } }]}
                  />
                ))}
              </Stepper>
            </Form>
          )}
        </Formik>
      </BookingFormContext.Provider>
    </ThemeProvider>
  )
})

export default BookingForm
